import { axiosClient, setAxiosHeaders } from '~/common/js/axios';
import {
  insertSignToParams,
  breakDownHostUrl,
  isLoginedToken,
  filterFalsityParams,
} from "~/common/js/utils";
import { transLang } from '~/common/js/storeUtil';
import get from "lodash-es/get";
import moment from "moment";
import Cookies from "js-cookie";
import NP from "number-precision";
import debounce from "lodash-es/debounce";
import numbro from "numbro";
import { defineStore } from "pinia";
import { useIndexStore } from "./index";
import {useTradeStore} from "./trade";
import { UserRemindEnum } from '@/models/account';
import { useCountryCode } from '~/utils/missionCenter';

const _getUserSecInfo = debounce(async function (cb) {
  if (!isLoginedToken()) {
    return;
  }
  const res = await axiosClient({
    url: "/gw-api/user-center/user-info",
    method: "get",
  });
  if (res?.success) {
    const cid = get(res, ["data", "accountInfo", "cid"]);
    if (window.sensors) {
      window.sensors.login(cid);
    }
    this.SET_BINDEMAIL(get(res, ["data", "accountInfo", "bindEmail"]) === "T");
    this.SET_BINDPHONE(get(res, ["data", "accountInfo", "bindPhone"]) === "T");
    this.SET_BINDGOOGLE(get(res, ["data", "accountInfo", "bindGoogle"]) === "T");
    this.SET_USEREMAIL(get(res, ["data", "accountInfo", "email"]));
    this.SET_USERPHONE(get(res, ["data", "accountInfo", "phone"]));
    this.SET_USERAREACODE(get(res, ["data", "accountInfo", "areaCode"]));
    this.SET_USERINTRONO(get(res, ["data", "accountInfo", "userIntroNo"]));
    // user-info 接口不提供了
    // commit('SET_USERINTROUSERTOTAL', ret.data.introUserTotal);
    this.SET_ACCOUNT_BASE(res.data);
    
    // 这个后面实现
    // commit("trade/SET_PRIVATE_COIN_LIST", null, { root: true });
    this.getIsVip();

    // 健壮 zendesk
    const cookieOptions = {
      expires: 30,
      httpOnly: false,
    };
    const webDomain = breakDownHostUrl(location.hostname);
    if (!webDomain.includes("localhost")) {
      Object.assign(cookieOptions, {
        domain: `.${webDomain}`,
      });
    }
    Cookies.set("jwtToken", res.data.jwtToken, cookieOptions);
  }

  cb && cb();
}, 0, {leading: true});

// console.log('_getUserSecInfo >>>', _getUserSecInfo);

export const useAccountStore = defineStore("account", {
  state: () => ({
    coinType: 18,
    depositHistoryList: null, // 充值历史记录
    withdrawHistoryList: null, // 提现历史记录
    financeWallet: {
      // 账户资产
      currencySymbol: "",
      sumAssetsBtc: null, // 折合总计
      sumAssetsLocal: null, // 折合总计估值
      frozenAssetsBtc: null, // 折合冻结
      frozenAssetsLocal: null, // 折合冻结估值
      availableAssetsBtc: null, // 折合可用
      availableAssetsLocal: null, // 折合可用估值
      dailyAssetEarn: null,
      coinTypes: [],
      coinMap: {},
    },
    contractOpen: null,
    /**
     * 合约账户信息
     * 目前里面的字段没有用，仅仅用来判断是否开通了合约
     */
    contractAccount: {
      apiKey: null,
      token: null,
      expiredTs: null,
    },
    // 是否开启合约交易
    contractTradeEnabled: false,
    // 合约资产
    contractWallet: {
      local: "en_US",
      currencySymbol: "USD",
      sumBalanceLocal: "0.00",
      sumBalanceBtc: "0.00000000",
      dailyEarnLocal: "0.00",
      walletList: [],
      localB: "en_US", // 后面加了B的都是币本位合约
      currencySymbolB: "USD",
      sumBalanceLocalB: "0.00",
      sumBalanceBtcB: "0.00000000",
      dailyEarnLocalB: "0.00",
      walletListB: [],
    },
    // 杠杆资产
    marginAsset: {
      symbolLoading: false,
      symbolList: [], // 杠杆交易对
      symbolMap: {}, // 交易字典
      coinLoading: false,
      coinList: [], // 币种
      coinMap: {}, // 币种字典
    },
    marginAssetData: {
      currencySymbol: "",
      leverSymbolList: [],
      sumAccountEquityBtc: "",
      sumAccountEquityLocal: "",
      sumAssetsBtc: "",
      sumAssetsLocal: "",
      sumDebtBtc: "",
      sumDebtLocal: "",
      sumInterestBtc: "",
      sumInterestLocal: "",
    },

    userEmail: null,
    userAreaCode: 1,

    userIntroNo: "*****",
    needBindEmailForWithdraw: false, // 是否强制提示要绑邮箱
    forceReplaceGAForSecurity: false, // 是否强制换绑GA
    needReplaceGAForOverdue: false, // 要换GA，不强制
    userIntroUserTotal: 0,

    bindEmail: null, // 绑定邮箱验证
    bindPhone: null, // 绑定手机短信验证
    bindGoogle: null, // 绑定谷歌验证

    loginName: null, // 这个值不是rootState里面的userName,这个值用来检测是否调用过getUserSecInfo
    telNumber: null, // 手机号码
    securityEmail: null, // 加密过的邮箱
    securityTelNumber: null, // 加密过的手机号码
    level: null, // 等级
    kycStatus: null, // 等级, 老的，不敢乱动
    kycLevel: 0,
    country: "",
    cid: "",
    fiatKycStatus: null, // 等级
    identityImgStatus: null, // 身份状态(0-未提交审核，1-审核成功，2-审核中)， 现在的kycStatus
    latestLoginTime: null, // 最后一次登录时间
    latestLoginIp: null, // 最后一次登录ip
    equipment: null, // 最后登录设备
    isClickShow2fa: null, // 用户登录后叉掉2fa弹窗
    levelDesc: [
      {
        titleLimit: 2,
      },
      {
        titleLimit: 100,
      },
      {
        titleLimit: -1,
      },
    ],
    isUserFeeDeducted: null, // 用户手续费是否抵扣状态
    afc: null, // 防钓鱼码
    maskAfc: null, // 防钓鱼码影藏几位

    rangeType: null, // 特殊用户类型 0-所有交易对  1-指定交易区  2-指定交易对 3-a,b类
    feeLevel: null, // 手续费等级
    feeLevelB: null, // B类手续费等级
    takerRate: null, // A 类吃单
    makerRate: null, // A 类挂单
    takerRateB: null, // B 类吃单
    makerRateB: null, // B 类挂单
    takerRateC: null, // C 类吃单
    makerRateC: null, // C 类挂单
    discountTakerRate: null, // A 类折扣吃单
    discountMakerRate: null, // A 类折扣挂单
    discountTakerRateB: null, // B 类折扣吃单
    discountMakerRateB: null, // B 类折扣挂单
    discountTakerRateC: null, // C 类折扣吃单
    discountMakerRateC: null, // C 类折扣挂单
    deductionDiscount: null, // 折扣 example 60%
    userRateType: null, // 0-普通用户(Normal) 1-专业用户（VIP） 2-特殊用户(SVIP)

    fiatSumAssetsLocal: "0",
    fiatSumAssetsBtc: "0",
    fiatCurrencySymbol: "",
    fiatWalletList: [],
    agentData: {}, // 合约代理商数据
    isAffiliate: false, // 是否现货合约代理商
    agentType: null, // 开通的代理商种类，1-现货代理商，2-合约代理商，3-合约及现货代理商
    mainId: null, // mainId>0则本账号为子账户
    isMarginOpen: null, // 杠杆是否开通
    futuresType: 2, // 用户合约类型，null 表示未初始化 -1未登陆，0是没有过kyc，1是币安合约，2是自研合约
    userId: null,
    copyTradeUserData: {}, // 用户跟单业务基本信息
    isVip: false,
    isTrial: false, // 是否是试用vip
    trialExpireDate: 0, // 试用期过期时间
    inVipList: false,
    vipLevel: 0, // vip等级
    hasDelegation: JSON.parse(Cookies.get('hasDelegation') || 'false'), // 是否有委托账户类型
    delegationType: JSON.parse(Cookies.get('delegationType') || '0'), // 当前账户类型是否为委托账户类型
    delegationTypeList: JSON.parse(Cookies.get('delegationTypeList') || '[]'), // 当前账户类型是否为委托账户类型  
    userName: '', //登录名称
    subAccountInfo: {},
    /** 用户红点提醒 */
    userReminds: [] as UserRemindEnum[],
  }),
  getters: {
    getLocaleFromIndex(state){
      const indexStore = useIndexStore();
      return indexStore.locale;
    },
    // 是否子账户
    isSubAccount: (state) => {
      return state.mainId > 0;
    },
    // 能否使用子账户功能
    canUseSubAccount: (state) => {
      // return false // 不显示子账户功能
      // return !(state.mainId > 0) && +state.kycLevel === 10;
      return !(state.mainId > 0) && !!state.subAccountInfo.enableSubAccounts;
    },
    isDelegation: (state) => {
      return state.hasDelegation && state.delegationType !== 0;
    },  
    is2FA: (state) => {
      let s = 0;
      if (state.bindGoogle) s++;
      if (state.bindPhone) s++;
      if (state.bindEmail) s++;
      return s > 1;
    },
    walletList: (state) => {
      const { coinTypes, coinMap } = state.financeWallet;
      return coinTypes.map((coinType) => coinMap[coinType]);
    },
    // 现货-隐私币
    walletListIsPrivate(){
      // console.log('walletListIsPrivate', this)
      const tradeStore = useTradeStore();
      const getterPrivateCoinList = tradeStore.getterPrivateCoinList; // rootGetters["trade/getterPrivateCoinList"];
      const coinIdsMap = new Map();
      const res = this.walletList.filter((item) => {
        // 过滤隐私币
        if (
          getterPrivateCoinList.find(
            (pitem) => +pitem.coinId === +item.coinType
          )
        ) {
          coinIdsMap.set(item.coinType, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: coinIdsMap,
      };
    },
    // 现货-排除隐私币并且资产为0的
    walletListIsPrivateAndNone(){
      const tradeStore = useTradeStore();
      const getterPrivateCoinList = tradeStore.getterPrivateCoinList; // rootGetters["trade/getterPrivateCoinList"];
      const coinIdsMap = new Map();
      const res = this.walletList.filter((item) => {
        // 过滤隐私币
        if (
          getterPrivateCoinList.find(
            (pitem) => +pitem.coinId === +item.coinType
          ) &&
          +item.totalBalance === 0
        ) {
          coinIdsMap.set(item.coinType, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: coinIdsMap,
      };
    },
    // 隐私法币列表
    fiatWalletListIsPrivate() {
      const tradeStore = useTradeStore();
      const getterPrivateCoinList = tradeStore.getterPrivateCoinList; // rootGetters["trade/getterPrivateCoinList"];
      const coinIdsMap = new Map();
      const res = this.fiatWalletList.filter((item) => {
        // 过滤隐私币
        if (
          getterPrivateCoinList.find((pitem) => +pitem.coinId === +item.coinId)
        ) {
          coinIdsMap.set(item.coinId, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: coinIdsMap,
      };
    },
    // 隐私和0资产法币列表
    fiatWalletListIsPrivateAndNone() {
      const tradeStore = useTradeStore();
      const getterPrivateCoinList = tradeStore.getterPrivateCoinList; // rootGetters["trade/getterPrivateCoinList"];
      const coinIdsMap = new Map();
      const res = this.fiatWalletList.filter((item) => {
        // 过滤隐私币
        if (
          getterPrivateCoinList.find(
            (pitem) => +pitem.coinId === +item.coinId
          ) &&
          +item.totalBalance === 0
        ) {
          coinIdsMap.set(item.coinId, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: coinIdsMap,
      };
    },
    // 隐私杠杆列表
    leverSymbolListIsPrivate() {
      const tradeStore = useTradeStore();
      const getterPrivateSymbolList = tradeStore.getterPrivateSymbolList;
        // rootGetters["trade/getterPrivateSymbolList"];
      const symbolIdsMap = new Map();
      const res = this.marginAssetData.leverSymbolList.filter((item) => {
        // 过滤隐私币
        if (getterPrivateSymbolList.includes(item.symbolId)) {
          symbolIdsMap.set(item.symbolId, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: symbolIdsMap,
      };
    },
    // 隐私和0资产杠杆列表
    leverSymbolListIsPrivateAndNone() {
      const tradeStore = useTradeStore();
      const getterPrivateSymbolList = tradeStore.getterPrivateSymbolList;
      const symbolIdsMap = new Map();
      const res = this.marginAssetData.leverSymbolList.filter((item) => {
        // 过滤隐私币
        if (
          getterPrivateSymbolList.includes(item.symbolId) &&
          +item.leverCoinList[0].totalBalance === 0 &&
          +item.leverCoinList[1].totalBalance === 0
        ) {
          symbolIdsMap.set(item.symbolId, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: symbolIdsMap,
      };
    },
    // 隐私和未借款杠杆列表
    leverSymbolListIsPrivateAndNoneBorrowed () {
      const tradeStore = useTradeStore();
      const getterPrivateSymbolList = tradeStore.getterPrivateSymbolList;
        // rootGetters["trade/getterPrivateSymbolList"];
      const symbolIdsMap = new Map();
      const res = this.marginAssetData.leverSymbolList.filter((item) => {
        // 过滤隐私币
        if (
          getterPrivateSymbolList.includes(item.symbolId) &&
          +item.leverCoinList[0].borrowed === 0 &&
          +item.leverCoinList[1].borrowed === 0
        ) {
          symbolIdsMap.set(item.symbolId, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: symbolIdsMap,
      };
    },
    // 隐私和未借款杠杆和0资产列表
    leverSymbolListIsPrivateNoAssetsUnBorrowed ()  {
      const tradeStore = useTradeStore();
      const getterPrivateSymbolList = tradeStore.getterPrivateSymbolList;
        // rootGetters["trade/getterPrivateSymbolList"];
      const symbolIdsMap = new Map();
      const res = this.marginAssetData.leverSymbolList.filter((item) => {
        // 过滤隐私币
        if (
          getterPrivateSymbolList.includes(item.symbolId) &&
          +item.leverCoinList[0].totalBalance === 0 &&
          +item.leverCoinList[1].totalBalance === 0 &&
          +item.leverCoinList[0].borrowed === 0 &&
          +item.leverCoinList[1].borrowed === 0
        ) {
          symbolIdsMap.set(item.symbolId, item);
          return true;
        }
        return false;
      });
      return {
        list: res,
        map: symbolIdsMap,
      };
    },
    /**
     * Map: 币名 -> 币Id
     */
    coinTypeByName: (state) => {
      const { coinTypes, coinMap } = state.financeWallet;
      return coinTypes.reduce((prev, coinType) => {
        if (coinMap[coinType]) {
          return Object.assign(prev, {
            [coinMap[coinType].coinShortName]: coinType,
          });
        }
        return prev;
      }, {});
    },
  },
  actions: {
    UPDATE_CONTRACT_INFO(data) {
      const { apiKey, token, expiredTs } = data;
      Object.assign(this.contractAccount, {
        apiKey,
        token,
        expiredTs,
      });
      const cookieDomain = breakDownHostUrl(location.hostname);
      const cookieOptions = {
        expires: 7,
      };
      if (!cookieDomain.includes("localhost")) {
        Object.assign(cookieOptions, {
          domain: `.${cookieDomain}`,
        });
      }
      Cookies.set("api_key", apiKey, cookieOptions);
      Cookies.set("token", token, cookieOptions);
      Cookies.set("expired_ts", expiredTs, cookieOptions);
    },
    UPDATE_CONTRACT_COPY_TRADE_INFO(data) {
      this.copyTradeUserData = data;
    },
    SET_HIS_COINTYPE(data) {
      console.log('SET_HIS_COINTYPE');
      this.coinType = data;
      console.log(this.coinType);
    },
    SET_DEPOSIT_HIS_LIST(data) {
      this.depositHistoryList = data;
    },
    SET_WITHDRAW_HIS_LIST(data) {
      this.withdrawHistoryList = data;
    },
    // 资产页面第二次渲染载入数字数据
    SET_FINANCE_WALLET_TOTAL(data) {
      Object.assign(this.financeWallet, {
        currencySymbol: data.currencySymbol,
        sumAssetsBtc: data.sumAssetsBtc,
        sumAssetsLocal: data.sumAssetsLocal,
        frozenAssetsBtc: data.frozenAssetsBtc,
        frozenAssetsLocal: data.frozenAssetsLocal,
        availableAssetsBtc: data.availableAssetsBtc,
        availableAssetsLocal: data.availableAssetsLocal,
        dailyAssetEarn: data.dailyAssetEarn,
      });
    },
    // 资产页面第二次渲染载入数字数据,更新相关的资产数据
    // 代码优化前，渲染速度test: 13720 ms
    // 代码优化后，渲染速度test: 1520 ms
    SET_FINANCE_WALLET(data) {
      const walletList = get(data, "walletList", []);
      const walletMap = walletList.reduce((map, item) => {
        map[item.coinType] = item;
        return map;
      }, {});

      const coinMap = this.financeWallet.coinTypes.reduce((map, coinType) => {
        const currentWalletCoin = walletMap[coinType];
        const financeWalletCoin = this.financeWallet.coinMap[coinType];

        map[coinType] = currentWalletCoin
          ? {
              ...financeWalletCoin,
              ...currentWalletCoin,
            }
          : financeWalletCoin;

        return map;
      }, {});

      this.financeWallet.coinMap = coinMap;
    },
    // 资产页面第一次渲染load图片、是否可充提等，让用户早点看到整体内容
    // 代码优化前，渲染速度test: 13720 ms
    // 代码优化后，渲染速度test: 1520 ms
    SET_FINANCE_WALLET_LIST(list) {
      const coinTypes = list.map((item) => item.coinType);
      const coinMap = list.reduce((map, item) => {
        map[item.coinType] = Object.assign({}, item, DEFAULT_COIN_BALANCE);
        return map;
      }, {});

      this.financeWallet.coinTypes = coinTypes;
      this.financeWallet.coinMap = coinMap;
    },
    SET_CONTRACT_WALLET(data) {
      // 设置合约资产相关的状态
      const {
        local,
        currencySymbol,
        sumBalanceLocal,
        sumBalanceBtc,
        dailyEarnLocal,
        localB,
        currencySymbolB,
        sumBalanceLocalB,
        dailyEarnLocalB,
        sumBalanceBtcB,
      } = data;
      const walletList = get(data, "walletList", []);
      const walletListB = get(data, "walletListB", []);
      Object.assign(this.contractWallet, {
        local,
        currencySymbol,
        sumBalanceLocal,
        sumBalanceBtc,
        dailyEarnLocal,
        walletList,
        localB,
        currencySymbolB,
        sumBalanceLocalB,
        sumBalanceBtcB,
        dailyEarnLocalB,
        walletListB,
      });
    },
    SET_MARGIN_ASSET_SYMBOL_LOADING(status) {
      this.marginAsset.symbolLoading = status;
    },
    // 杠杆资产交易对列表更新
    SET_MARGIN_ASSET_SYMBOL_LIST(payload) {
      this.marginAsset.symbolList = payload;

      this.marginAsset.symbolMap = payload.reduce((map, item) => {
        map[item.tradeMappingId] = item;
        return map;
      }, {});
    },
    SET_MARGIN_ASSET_COIN_LOADING(status) {
      this.marginAsset.coinLoading = status;
    },
    // 杠杆资产币种列表更新
    SET_MARGIN_ASSET_COIN_LIST(payload) {
      this.marginAsset.coinList = payload;
      this.marginAsset.coinMap = payload.reduce((map, item) => {
        map[item.coinType] = item;
        return map;
      }, {});
    },
    SET_USEREMAIL(email) {
      this.userEmail = email;
      if (email) {
        this.securityEmail = email.replace(
          /^([^@]{0,3}).+(@[^@]+)$/,
          "$1***$2"
        );
      } else {
        this.securityEmail = null;
      }
    },
    SET_USERPHONE(telNumber) {
      this.telNumber = telNumber;
      if (telNumber) {
        this.securityTelNumber = `${telNumber.substring(0, 4)  }****`;
      } else {
        this.securityTelNumber = null;
      }
    },
    SET_USERAREACODE(areacode) {
      this.userAreaCode = areacode;
    },
    SET_BINDEMAIL(data) {
      this.bindEmail = data;
    },
    SET_BINDPHONE(data) {
      this.bindPhone = data;
    },
    SET_BINDGOOGLE(data) {
      this.bindGoogle = data;
    },
    SET_USERINTRONO(data) {
      this.userIntroNo = data;
    },
    SET_ANTIPHISHING(data) {
      this.afc = data.afc;
      this.maskAfc = data.maskAfc;
    },
    SET_USER_EXTRA(data) {
      this.needBindEmailForWithdraw = data.needBindEmailForWithdraw;
      this.forceReplaceGAForSecurity = data.forceReplaceGAForSecurity;
      this.needReplaceGAForOverdue = data.needReplaceGAForOverdue;
    },
    SET_USERINTROUSERTOTAL(data) {
      this.userIntroUserTotal = data;
    },
    SET_ACCOUNT_BASE(data) {
      this.loginName = get(data, ["accountInfo", "loginName"]);
      this.cid = get(data, ["accountInfo", "cid"]);
      this.mainId = get(data, ["accountInfo", "mainId"]);
      this.userId = get(data, ["accountInfo", "userId"]);
      this.contractTradeEnabled =
        get(data, ["accountInfo", "contractTradeEnabled"]) === "T";

      this.level = get(data, ["levelInfo", "level"]);
      this.kycStatus = get(data, ["levelInfo", "kycStatus"]);
      this.fiatKycStatus = get(data, ["levelInfo", "fiatKycStatus"]);
      this.ignoreKyc = [100, 101].includes(+this.level); // 忽略kyc校验的平台用户和机构用户
      this.identityImgStatus = get(data, ["kycInfo", "kycStatus"]);
      this.kycLevel = get(data, ["kycInfo", "kycLevel"]);

      const retCountry = get(data, ["kycInfo", "country"]);
      // todo
      // const retCountry = 'AL'
      this.country = retCountry;

      if (retCountry) {
        // 这个暂时只用来做隐藏币种的需求
        window.currentCountryCode = retCountry;
      }

      this.latestLoginTime = moment(
        get(data, ["logInfo", "latestLoginTimestamp"])
      ).format("YYYY-MM-DD HH:mm:ss");
      this.latestLoginIp = get(data, ["logInfo", "latestLoginIp"]);
      this.levelDesc = get(data, ["levelInfo", "levelDesc"]);
      this.equipment = get(data, ["logInfo", "latestLoginEquipment"]);
    },
    SET_USER_FEE_DEDUCTION(data) {
      this.isUserFeeDeducted = data;
    },
    SET_CLICK_SHOW_2FA_ACTION(data) {
      this.isClickShow2fa = data;
    },
    SET_FEE_LEVEL(data) {
      this.feeLevel = data.level;
      // 用户b类等级 - inm add
      this.feeLevelB = data.levelB;
      this.rangeType = data.rangeType;
      this.userRateType = data.userRateType;
      this.takerRate = `${numbro(data.takerRateClass1)
        .multiply(100)
        .value()}%`;
      this.makerRate = `${numbro(data.makerRateClass1)
        .multiply(100)
        .value()}%`;
      this.takerRateB = `${numbro(data.takerRateClass2 || 0)
        .multiply(100)
        .value()}%`;
      this.makerRateB = `${numbro(data.makerRateClass2 || 0)
        .multiply(100)
        .value()}%`;
      this.makerRateC = `${numbro(data.makerRateClass3 || 0)
        .multiply(100)
        .value()}%`;
      this.takerRateC = `${numbro(data.takerRateClass3 || 0)
        .multiply(100)
        .value()}%`;
      this.discountTakerRate = `${numbro(+data.discountTakerRateClass1)
        .multiply(100)
        .value()}%`;
      this.discountMakerRate = `${numbro(+data.discountMakerRateClass1)
        .multiply(100)
        .value()}%`;

      this.discountTakerRateB = `${numbro(+data.discountTakerRateClass2 || 0)
        .multiply(100)
        .value()}%`;
      this.discountMakerRateB = `${numbro(+data.discountMakerRateClass2 || 0)
        .multiply(100)
        .value()}%`;

      this.discountTakerRateC = `${numbro(+data.discountTakerRateClass3 || 0)
        .multiply(100)
        .value()}%`;
      this.discountMakerRateC = `${numbro(+data.discountMakerRateClass3 || 0)
        .multiply(100)
        .value()}%`;

      const disc = data.deductionDiscount || 1;
      this.deductionDiscount = `${NP.strip((1 - disc) * 100)}%`;
    },
    SET_FIAT_WALLET(data) {
      this.fiatSumAssetsLocal = data.sumAssetsLocal;
      this.fiatSumAssetsBtc = data.sumAssetsBtc;
      this.fiatWalletList = data.walletList;
      this.fiatCurrencySymbol = data.currencySymbol;
    },
    SET_AGENT_DATA(data) {
      this.agentData = { ...this.agentData, ...data };
    },
    SET_AGENT_DATA_INVITE(data) {
      this.agentData = { ...this.agentData, ...data };
    },
    SET_IS_AFFILIATE(isAffiliate) {
      this.isAffiliate = isAffiliate;
    },
    SET_AGENT_TYPE(agentType) {
      this.agentType = agentType;
    },
    // 设置杠杆开户状态
    SET_MARGIN_ISOPEN(isOpen) {
      this.isMarginOpen = isOpen;
    },
    // SET_FUTURES_TYPE( futuresType) {
    //   this.futuresType = futuresType
    // },
    SET_CONTRACT_OPEN(data) {
      this.contractOpen = data;
    },
    SET_MARGIN_ASSET_DATA(data) {
      this.marginAssetData = data;
    },
    // 设置是否存在委托账户
    SET_HAS_DELEGATION(data) {
      this.hasDelegation = data;
    },
    // 设置委托账户类型
    SET_DELEGATION_TYPE(data) {
      this.delegationType = data;
    },
    // 设置委托账户类型列表
    SET_DELEGATION_TYPE_LIST(data) {
      this.delegationTypeList = data;
    },
    // 设置是否是vip
    SET_IS_VIP(isVip: boolean) {
      this.isVip = isVip;
    },
    SET_IS_TRIAL(trial: number) {
      this.isTrial = trial === 2;
    },
    SET_TRIAL_EXPIRE_DATE(date: number) {
      this.trialExpireDate = date;
    },
    // 设置是否lock页面
    SET_IS_VIPLIST(inVipList: boolean) {
      this.inVipList = inVipList;
    },
    // 设置vip等级
    SET_IS_VIPLEVEL (vipLevel: number) {
      this.vipLevel = vipLevel;
    },
    // 设置登录名称
    SET_LOGIN_NAME (name) {
      this.userName = name;
    },
    // 设置子账户信息
    SET_SUBACCOUNT_INFO(data) {
      this.subAccountInfo = data || {};
    },
    async getContractStatus() {
      if (!isLoginedToken()) {
        return;
      }
      // TODO 3619
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      const params = {
        timestamp: Date.now(),
      };
      insertSignToParams(params, params.timestamp, salt);
      try {
        const res = await axiosClient({
          method: "GET",
          url: "/gw-api/asset/swap/account-query",
          params,
          // headers: {
          //   Authorization: token,
          // },
          transformResponse: (data) => JSON.parse(data),
        });
        if (res?.success) {
          this.UPDATE_CONTRACT_INFO(res.data);
          // commit("UPDATE_CONTRACT_INFO", res.data);
          return res.data;
        }
      } catch (err) {
        // ignore
      }
    },
    async getContractCopyTradeStatus() {
      if (!isLoginedToken()) {
        return;
      }
      // TODO 3619
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      const params = {
        timestamp: Date.now(),
      };
      insertSignToParams(params, params.timestamp, salt);
      try {
        const res = await axiosClient({
          method: "GET",
          url: "/gw-api/contract-tiger/forward/v1/ifcontract/copy/trade/user/info",
          params,
          // headers: {
          //   Authorization: token,
          // },
          transformResponse: (data) => JSON.parse(data),
        });
        const res2 = await axiosClient({
          method: "GET",
          url: "/gw-api/contract-tiger/account/query",
          params,
          // headers: {
          //   Authorization: token,
          // },
          transformResponse: (data) => JSON.parse(data),
        });
        if (res?.success || res2?.success) {
          this.UPDATE_CONTRACT_COPY_TRADE_INFO({
            ...(res2.data || {}),
            ...(res.data || {}),
          });
          // commit("UPDATE_CONTRACT_COPY_TRADE_INFO", {
          //   ...(res2.data || {}),
          //   ...(res.data || {}),
          // });
          return { ...(res2.data || {}), ...(res.data || {}) };
        }
      } catch (err) {
        // ignore
      }
    },
    /**
     * [getHistories 获取充提历史记录]
     * @param  {[type]} payload   [description]
     */
    async getHistories(payload) {
      if (!isLoginedToken()) {
        return;
      }
      // TODO 3619
      const indexStore = useIndexStore();
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      const lang = this.getLocaleFromIndex;
      const langParam = transLang(lang, indexStore);
      const params = {
        local: langParam,
        coinType: this.coinType,
        operationType: payload.operationType, // 1-充值 2-提现
        currentPage: payload.currentPage,
        // sizePage: payload.pageSize,
        sizePage: 10,
        timestamp: Date.now(),
      };
      insertSignToParams(params, params.timestamp, salt);
      const ret = await axiosClient({
        url: "/gw-api/api/v3/account_coin_bill",
        method: "GET",
        params,
        // headers: {
        //   Authorization: token,
        // },
      });
      if (ret?.success) {
        if (payload.operationType === 1) {
          this.SET_DEPOSIT_HIS_LIST(ret.data);
        }
        if (payload.operationType === 2) {
          this.SET_WITHDRAW_HIS_LIST(ret.data);
        }
      }
    },

    /**
     * getFinanceWallet 获取钱包所有钱包信息
     * @param state
     * @param commit
     * @param rootState
     * @returns {Promise<void>}
     */
    async getFinanceWallet() {
      console.log("getFinanceWallet >>>>");
      if (!isLoginedToken()) {
        return;
      }
      // TODO 3619
      const indexStore = useIndexStore();
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      const lang = this.getLocaleFromIndex;
      const params = {
        local: transLang(lang, indexStore),
        timestamp: new Date().getTime(),
      };
      insertSignToParams(params, params.timestamp, salt);
      try {
        const ret = await axiosClient({
          url: "/gw-api/asset-gateway/asset/coin",
          method: "get",
          // headers: {
          //   Authorization: token,
          // },
          params,
        });
        if (ret && ret?.success) {
          if (Array.isArray(ret.data.assetCoinList)) {
            this.SET_FINANCE_WALLET_LIST(ret.data.assetCoinList);
            // commit("SET_FINANCE_WALLET_LIST", ret.data.assetCoinList);
          }
          // await dispatch("getFinanceWalletDetail"); //加载全部数据的请求只能异步请求
          await this.getFinanceWalletDetail();
        }
      } catch (err) {
        // ignore
      }
    },
    /**
     * getFinanceWallet 获取钱包所有钱包信息
     * @param state
     * @param commit
     * @param rootState
     * @returns {Promise<void>}
     */
    async getFinanceWalletDetail() {
      if (!isLoginedToken()) {
        return;
      }
      // TODO 3619
      const indexStore = useIndexStore();
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      const lang = this.getLocaleFromIndex;
      const params = {
        local: transLang(lang, indexStore),
        timestamp: new Date().getTime(),
      };
      insertSignToParams(params, params.timestamp, salt);
      const ret = await axiosClient({
        url: "/gw-api/asset-gateway/spot/balance", // 新的钱包接口
        method: "get",
        // headers: {
        //   Authorization: token,
        // },
        params,
      });
      if (ret?.success) {
        // commit("SET_FINANCE_WALLET_TOTAL", ret.data);
        // commit("SET_FINANCE_WALLET", ret.data);
      }
    },
    /**
     * getSymbolList 获取杠杆资产的交易对信息
     * @param state
     * @param commit
     * @param rootState
     * @returns {Promise<void>}
     */
    async getSymbolList(reset = false) {
      if (!isLoginedToken()) {
        return;
      }

      if (this.marginAsset.symbolLoading) return;

      // 已存在且不重置时，不重复请求
      if (!reset && this.marginAsset.symbolList.length > 0) return;

      this.SET_MARGIN_ASSET_SYMBOL_LOADING(true);
      // commit("SET_MARGIN_ASSET_SYMBOL_LOADING", true);

      // const token = Cookies.get('accessToken')
      // const salt = Cookies.get('accessSalt')
      // const lang = rootState.locale

      // const params = {
      //   // local: transLang(lang),
      //   // timestamp: new Date().getTime(),
      // }

      const resData = await axiosClient({
        // @TODO 这里接口地址需要变 ，如变成 /find_leverage_symbol_list？
        url: "/gw-api/ds/find_leverage_symbol_list",
        method: "GET",
        // headers: {
        //   Authorization: token,
        // },
        // params,
      }).finally(() => {
        this.SET_MARGIN_ASSET_SYMBOL_LOADING(false);
        // commit("SET_MARGIN_ASSET_SYMBOL_LOADING", false);
      });

      if (resData && resData.success) {
        this.SET_MARGIN_ASSET_SYMBOL_LIST(resData.data);
        // commit("SET_MARGIN_ASSET_SYMBOL_LIST", resData.data);
      }
    },
    /**
     * getCoinList 获取钱包所有币种信息
     * @param state
     * @param commit
     * @param rootState
     * @returns {Promise<void>}
     */
    async getCoinList(reset = false) {
      if (!isLoginedToken()) {
        return;
      }

      if (this.marginAsset.coinLoading) return;

      // 已存在且不重置时，不重复请求
      if (!reset && this.marginAsset.coinList.length > 0) return;

      this.SET_MARGIN_ASSET_COIN_LOADING();
      this.SET_MARGIN_ASSET_COIN_LOADING(true);

      // const token = Cookies.get('accessToken')
      const salt = Cookies.get("accessSalt");
      const lang = this.getLocaleFromIndex;

      const indexStore = useIndexStore();
      const params = {
        local: transLang(lang, indexStore),
        timestamp: new Date().getTime(),
      };

      insertSignToParams(params, params.timestamp, salt);

      const resData = await axiosClient({
        url: "/gw-api/asset-gateway/asset/coin",
        method: "get",
        // headers: {
        //   Authorization: token,
        // },
        params,
      }).finally(() => {
        this.SET_MARGIN_ASSET_COIN_LOADING(false);
      });

      if (resData && resData.success) {
        this.SET_MARGIN_ASSET_COIN_LIST(resData.data?.assetCoinList);
      }
    },
    async getContractWallet() {
      if (!isLoginedToken()) {
        return;
      }
      // TODO 3619
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      const indexStore = useIndexStore();
      const params = {
        /**
         * 计价货币
         */
        // currency: 'USD',
        timestamp: Date.now(),
        local: transLang(this.getLocaleFromIndex, indexStore),
      };
      insertSignToParams(params, params.timestamp, salt);
      let resU = null; // 装U本位的请求结果，这么做是因为后端的接口无法同时获得U本位和币本位的总资产
      let resB = null; // 装B本位的请求结果
      resU = await axiosClient({
        // 这个是U本位的请求
        url: "/gw-api/asset-gateway/contract/balance",
        params,
        // headers: {
        //   Authorization: token,
        // },
      });
      resB = await axiosClient({
        url: "/gw-api/asset-gateway/contract/balance",
        params: { ...params, apiType: 1 }, // 得到合约资产 currency 表示计价货币 type 0表示U本位 1表示币本位分不传就是U 2是全部
        headers: {
          Authorization: token,
        },
      });

      const finalData = {
        local: "",
        currencySymbol: "",
        sumBalanceLocal: "",
        sumBalanceBtc: "",
        dailyEarnLocal: "",
        walletList: [],
        localB: "", // 后面加了B的都是币本位合约
        currencySymbolB: "",
        sumBalanceLocalB: "",
        sumBalanceBtcB: "",
        dailyEarnLocalB: "",
        walletListB: [],
      };
      // 这里将两个请求结果合并处理
      if (resU?.success) {
        finalData.local = resU.data.local;
        finalData.currencySymbol = resU.data.currencySymbol;
        finalData.sumBalanceLocal = resU.data.sumBalanceLocal;
        finalData.sumBalanceBtc = resU.data.sumBalanceBtc;
        finalData.dailyEarnLocal = resU.data.dailyEarnLocal;
        finalData.walletList = resU.data.walletList;
      }
      if (resB?.success) {
        finalData.localB = resB.data.local;
        finalData.currencySymbolB = resB.data.currencySymbol;
        finalData.sumBalanceLocalB = resB.data.sumBalanceLocal;
        finalData.sumBalanceBtcB = resB.data.sumBalanceBtc;
        finalData.dailyEarnLocalB = resB.data.dailyEarnLocal;
        finalData.walletListB = resB.data.walletList;
      }
      // 最终提交状态修改
      this.SET_CONTRACT_WALLET(finalData);
    },
    /**
     * [getHistories 获取绑定情况]
     * @param
     */
    getUserSecInfo: _getUserSecInfo,

    async getUserFishCode() {
      if (!isLoginedToken()) {
        return;
      }
      // const token = Cookies.get("accessToken");
      const res = await axiosClient({
        url: "/gw-api/user-center/anti-fishing-code/get",
        method: "get",
        // headers: {
        //   Authorization: token,
        // },
      });
      if (res?.success) {
        this.SET_ANTIPHISHING(get(res, ["data"]));
      }
    },
    /**
     * 跟 user-info 区别
     * 获取用户其他额外信息，比如是否要GA换绑，是否要提示强制做某些事情
     */
    async getUserBindInfo() {
      if (!isLoginedToken()) {
        return;
      }
      // const token = Cookies.get("accessToken");
      const ret = await axiosClient({
        url: "/gw-api/user-center/user-bind",
        method: "get",
        // headers: {
        //   Authorization: token,
        // },
      });
      if (ret && ret?.success) {
        this.SET_USER_EXTRA(ret.data);
      }

      // if (ret?.success) {
      //   commit('SET_FEE_LEVEL', ret.data)
      // }
    },
    /**
     * [getFeeDeduction 获取用户是否打开手续费抵扣开关]
     * @param
     */
    async getUserFeeDeduction() {
      if (!isLoginedToken()) {
        return;
      }
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      const params = {
        timestamp: new Date().getTime(),
      };
      insertSignToParams(params, params.timestamp, salt);

      const ret = await axiosClient({
        method: "GET",
        url: "/gw-api/spot-fee/user/queryDecuction",
        params,
        // headers: {
        //   Authorization: token,
        // },
      });
      if (ret?.success) {
        this.SET_USER_FEE_DEDUCTION(+ret.data.deduction === 1);
      }
    },

    async getFiatBalance() {
      console.log("getFiatBalance >>>>>>>>");
      const lang = this.getLocaleFromIndex;
      const indexStore = useIndexStore();
      const params = {
        local: transLang(lang, indexStore),
        timestamp: new Date().getTime(),
      };
      const ret = await axiosClient({
        method: "GET",
        url: "/gw-api/asset-gateway/fiat/balance",
        params,
        // headers: {
        //   Authorization: Cookies.get("accessToken"),
        // },
      });

      if (ret?.success) {
        this.SET_FIAT_WALLET(ret.data);
      }
    },
    // 获取代理商相关数据
    // @注意：这个action只是用来获取合约代理商相关数据
    async getIsAgent() {
      // const token = Cookies.get("accessToken");
      const salt = Cookies.get("accessSalt");
      // 由于后端设计失误，邀请信息需要从另外的接口获得
      axiosClient({
        method: "GET",
        url: "/gw-api/user-center/front/invite/getUserInviteByUserId",
        // headers: {
        //   Authorization: token,
        // },
        params: {
          currentPage: 1,
          pageSize: 5,
        },
      }).then((res) => {
        if (res?.success && res?.data?.inviteCodeList?.length > 0) {
          this.SET_AGENT_DATA_INVITE(res.data.inviteCodeList[0]);
        }
      });

      const params = {
        timestamp: new Date().getTime(),
      };
      insertSignToParams(params, params.timestamp, salt);
      const res = await axiosClient({
        method: "GET",
        url: "/gw-api/contract-tiger/agency/v1/api/customer/user/info",
        params,
        // headers: {
        //   Authorization: token,
        // },
      });
      if (res?.success) {
        this.SET_AGENT_DATA(res.data);
        return res.data;
      }
    },

    // 是否现货合约代理商
    async getIsAffiliate() {
      const res = await axiosClient({
        method: "GET",
        url: "/gw-api/user-center/front/agent/queryIsAgent",
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
        },
      });

      if (res?.success) {
        this.SET_AGENT_TYPE(res.data?.agentType);
        // 0否 1是
        return this.SET_IS_AFFILIATE(res.data?.isAgent === 1);
      }
    },

    /**
     * [getUserTradeLevelInfo 获取用户BMX折扣，手续费等级 及 基础手续费]
     * 主要是BMX折扣，因为全局都要用到
     */
    async getUserTradeLevelInfo() {
      const timestamp = new Date().getTime();
      const params = {
        timestamp,
      };
      if (!isLoginedToken()) {
        return;
      }
      // const token = Cookies.get("accessToken");

      const ret = await axiosClient({
        method: "GET",
        url: "/gw-api/spot-fee/queryUserSpotFeeRate",
        params,
        // headers: {
        //   Authorization: token,
        // },
      });

      if (ret?.success) {
        this.SET_FEE_LEVEL(ret.data);
      }
    },
    // 获取用户杠杆是否开通
    async getMarginAccountOpened() {
      axiosClient({
        method: "GET",
        url: "/gw-api/user-center/user/ext/accountOpened",
      }).then((res) => {
        if (res?.success) {
          this.SET_MARGIN_ISOPEN(!!res.data);
        }
      });
    },
    async getContractOpen() {
      const ret = await axiosClient({
        method: "GET",
        url: "/gw-api/contract-tiger/account/query",
        // headers: {
        //   Authorization: Cookies.get("accessToken"),
        // },
      });
      if (ret?.success) {
        this.SET_CONTRACT_OPEN(ret.data);
      }
    },
    // 获取杠杆资产列表
    async getMarginAssetsList() {
      const indexStore = useIndexStore();
      const resData = await axiosClient({
        method: "GET",
        url: "/gw-api/asset-gateway/leverage/balance",
        // filterFalsityParams过滤掉假值：如undefined，null，空字符串等
        params: filterFalsityParams(
          {
            local: transLang(this.getLocaleFromIndex, indexStore),
          },
          true
        ),
      });

      if (resData?.success) {
        this.SET_MARGIN_ASSET_DATA(resData.data);
      }
    },
    // 获取用户是否是vip
    async getIsVip() {
      const resData = await axiosClient({
        method: 'GET',
        url: '/gw-api/user-center/front/user/aggInfo',
        params: {
          queryVip: true,
          querySubAccount: true,
          queryDelegationAccount: true,
        },
        // headers: {
        //   Authorization: Cookies.get("accessToken"),
        // },
      });

      if (resData?.success) {
        const vipLevel = resData?.data?.userVipInfo?.vipLevel;
        this.SET_IS_VIPLEVEL(vipLevel);
        this.SET_IS_VIP(Boolean(resData?.data?.userVipInfo?.isVip));
        this.SET_IS_TRIAL(resData?.data?.userVipInfo?.vipStatus);
        this.SET_TRIAL_EXPIRE_DATE(resData?.data?.userVipInfo?.expireDate);
        this.SET_IS_VIPLIST(Boolean(resData?.data?.userVipInfo?.inVipList));
        this.SET_LOGIN_NAME(resData?.data?.userInfo?.loginName);
        this.SET_SUBACCOUNT_INFO(resData?.data?.subAccountInfo);

        const hasDelegation = Boolean(resData?.data?.delegationAccount?.isDelegation);
        const delegationType = resData?.data?.delegationAccount?.accountSwitchStatus || 0;
        const delegationTypeList = resData?.data?.delegationAccount?.delegationTypeList || [];
        this.SET_HAS_DELEGATION(hasDelegation);
        this.SET_DELEGATION_TYPE(delegationType);
        this.SET_DELEGATION_TYPE_LIST(delegationTypeList);

        const cookieOptions = {
          expires: 30,
          httpOnly: false,
        };

        Cookies.remove('hasDelegation');
        Cookies.remove('delegationType');
        Cookies.remove('delegationTypeList');
        Cookies.set('hasDelegation', JSON.stringify(hasDelegation), cookieOptions);
        Cookies.set('delegationType', JSON.stringify(delegationType), cookieOptions);
        Cookies.set('delegationTypeList', JSON.stringify(delegationTypeList), cookieOptions);

        const agency = hasDelegation ? delegationType || 0 : 0;

        setAxiosHeaders({
          AGENCY: String(agency),
        });
      }
    },

    async getVoucherRemind() {
      const removeVoucher = () => {
        this.userReminds = (this.userReminds || []).filter((item: UserRemindEnum) => {
          return item !== UserRemindEnum.Voucher;
        });
      };
      if (!isLoginedToken()) {
        removeVoucher();
        return;
      }
      // 需要country信息
      const { getCountryCode } = useCountryCode();
      const countryCode = await getCountryCode(useIndexStore());
      const lang = this.getLocaleFromIndex;
      axiosClient({
        url: '/gw-api/mission/module/voucher/user-red-point',
        method: 'GET',
        params: {
          language: transLang(lang),
          country: countryCode,
        },
      }).then((resp: any) => {
        if (resp?.success) {
          const { showFlag } = resp?.data || {};
          if (showFlag) {
            this.userReminds  = [...(this.userReminds || []), UserRemindEnum.Voucher];
          } else {
            removeVoucher();
          }
        }
      });
    },
  },
});
